/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import {
  Button,
  Grid,
  Group,
  TextInput,
  Select,
  Loader,
  Center,
  Radio,
  ActionIcon,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useForm, zodResolver } from "@mantine/form";
import { useAddHolidays } from "api/holiday/useAddHolidays";
import { useUser } from "hooks/useUser";
import { z } from "zod";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetOrganizations } from "api/superAdmin/organizationManagement";
import { useGetPrograms } from "api/program/getprogram";

const today = new Date().toISOString().split("T")[0];

const addHolidaySchema = z
  .object({
    title: z.string().trim().min(1, { message: "Please enter holiday title" }),
    dateMode: z.enum(["multiple", "range"]).default("multiple"),
    dates: z.array(z.string()).optional(),
    dateRange: z.array(z.date().nullable()).optional(),
    departmentId: z.string().optional().nullable(),
    orgId: z.string().optional().nullable(),
    programId: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.dateMode === "multiple") {
      const validDates = data.dates?.filter(d => d.trim() !== "");
      if (!validDates || validDates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter at least one holiday date",
          path: ["dates", 0],
        });
      } else {
        const current = new Date(new Date().toDateString());
        validDates.forEach((val, index) => {
          const selected = new Date(val);
          if (selected < current) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Holiday date cannot be in the past",
              path: ["dates", index],
            });
          }
        });
      }
    } else {
      if (!data.dateRange || !data.dateRange[0] || !data.dateRange[1]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select a valid date range",
          path: ["dateRange"],
        });
      }
    }
  });

type Props = {
  onClose: () => void;
  programId?: string;
};

export default function AddHolidayForm({ onClose, programId }: Props) {
  const { user } = useUser();
  const { mutate: addHoliday, isLoading } = useAddHolidays();

  const { data: programsResp, isLoading: loadingPrograms } = useGetPrograms();
  const { data: departmentsRaw, isLoading: depsLoading } = useGetDepartments();
  const { data: orgsResponse, isLoading: orgsLoading } = useGetOrganizations();

  const departmentsSource = departmentsRaw?.data ?? departmentsRaw;
  const departments: any[] = Array.isArray(departmentsSource)
    ? departmentsSource
    : [];

  const departmentOptions =
    departments.map((d: any) => ({
      value: String(d?._id ?? d?.id ?? ""),
      label: String(d?.name ?? d?.label ?? ""),
    })) ?? [];

  const apiOrgOptions =
    orgsResponse?.organizations && Array.isArray(orgsResponse.organizations)
      ? orgsResponse.organizations.map((o: any) => ({
        value: String(o._id ?? o.id ?? ""),
        label: o.name ?? "Unknown",
      }))
      : [];

  const programsSource = programsResp?.data ?? programsResp;
  const programs: any[] = Array.isArray(programsSource) ? programsSource : [];

  const programOptions =
    programs.map((p: any) => ({
      value: String(p?._id ?? p?.id ?? ""),
      label: String(p?.name ?? p?.title ?? p?.label ?? "Unnamed Program"),
    })) ?? [];

  const orgOptions =
    apiOrgOptions.length > 0
      ? apiOrgOptions
      : user?.org
        ? [
          {
            value: String(user.org._id ?? user.org._id ?? ""),
            label: user.org.name ?? "Organization",
          },
        ]
        : [];

  const form = useForm({
    initialValues: {
      title: "",
      dateMode: "multiple",
      dates: [""],
      dateRange: [null, null] as [Date | null, Date | null],
      departmentId: "",
      orgId: user?.org?._id ? String(user.org._id) : "",
      programId: programId ?? "",
    },
    validate: zodResolver(addHolidaySchema),
  });

  useEffect(() => {
    if (programId) form.setFieldValue("programId", programId);
  }, [programId]);

  useEffect(() => {
    const userOrgId = user?.org?._id ?? user?.org?._id ?? "";
    if (userOrgId && !form.values.orgId) {
      form.setFieldValue("orgId", String(userOrgId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = (values: typeof form.values) => {
    const assignedTo: string[] = [];
    if (values.programId) assignedTo.push("program");
    if (values.departmentId) assignedTo.push("department");
    if (values.orgId) assignedTo.push("organization");

    let finalDates: string[] = [];

    if (values.dateMode === "multiple") {
      finalDates = (values.dates || []).filter(d => d.trim() !== "");
    } else if (values.dateMode === "range" && values.dateRange && values.dateRange[0] && values.dateRange[1]) {
      const start = new Date(values.dateRange[0]);
      const end = new Date(values.dateRange[1]);
      let current = new Date(start);
      while (current <= end) {
        finalDates.push(current.toISOString().split("T")[0]);
        current.setDate(current.getDate() + 1);
      }
    }

    const payload: any = {
      title: values.title.trim(),
      dates: Array.from(new Set(finalDates)), // Remove duplicates if any
      assignedTo,
    };

    if (values.programId) payload.programId = values.programId;
    if (values.departmentId) payload.departmentId = values.departmentId;
    if (values.orgId) payload.orgId = values.orgId;

    addHoliday(payload);
    onClose();
  };

  if (depsLoading || orgsLoading || loadingPrograms) {
    return (
      <Center style={{ padding: 20 }}>
        <Loader />
      </Center>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            withAsterisk
            label="Holiday Name"
            {...form.getInputProps("title")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Radio.Group
            label="Date Type"
            withAsterisk
            {...form.getInputProps("dateMode")}
          >
            <Group mt="xs">
              <Radio value="multiple" label="Specific Dates" />
              <Radio value="range" label="Date Range" />
            </Group>
          </Radio.Group>
        </Grid.Col>

        {form.values.dateMode === "multiple" ? (
          <Grid.Col span={12}>
            {form.values.dates.map((_, index) => (
              <Group key={index} mt={index > 0 ? "sm" : 0} align="flex-end">
                <TextInput
                  type="date"
                  label={index === 0 ? "Holiday Date(s)" : ""}
                  min={today}
                  style={{ flex: 1 }}
                  {...form.getInputProps(`dates.${index}`)}
                />
                {form.values.dates.length > 1 && (
                  <ActionIcon
                    color="red"
                    onClick={() => form.removeListItem("dates", index)}
                    title="Remove date"
                    mb={4}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                )}
              </Group>
            ))}
            <Button
              variant="subtle"
              size="xs"
              mt="sm"
              leftIcon={<IconPlus size={14} />}
              onClick={() => form.insertListItem("dates", "")}
            >
              Add another date
            </Button>
          </Grid.Col>
        ) : (
          <Grid.Col span={12}>
            <DateRangePicker
              label="Holiday Date Range"
              placeholder="Pick dates range"
              withAsterisk
              minDate={new Date()}
              {...form.getInputProps("dateRange")}
            />
          </Grid.Col>
        )}

        <Grid.Col span={12}>
          <Select
            label="Department (for department-specific holidays)"
            placeholder="Select a department"
            description="Leave empty for organization-wide or program-specific holidays"
            data={departmentOptions}
            searchable
            clearable
            {...form.getInputProps("departmentId")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Select
            label="Organization (for organization-wide holidays)"
            placeholder="Select organization"
            description="Select your organization for holidays visible to all employees"
            data={orgOptions}
            searchable
            clearable
            {...form.getInputProps("orgId")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Select
            label="Program (for program-specific holidays)"
            placeholder="Select program"
            description="Leave empty for organization-wide or department-specific holidays"
            data={programOptions}
            searchable
            clearable
            {...form.getInputProps("programId")}
          />
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          disabled={!form.isDirty()}
          loading={isLoading}
        >
          Add Holiday
        </Button>
      </Group>
    </form>
  );
}
